/**
 * Module de hashing d'identifiants pour masquer les UUIDs dans les URLs.
 * 
 * Compatible avec le backend Python (db/hashid.py).
 * Utilise Sqids pour encoder/décoder les UUIDs.
 * 
 * @example
 * import { encodeId, decodeId } from '@/common/hashid';
 * 
 * // Encoder un UUID
 * const hashed = encodeId("550e8400-e29b-41d4-a716-446655440000");
 * // => "3QDxWZb4hFdpAyzTCzAvwFx4PDH8"
 * 
 * // Décoder
 * const uuid = decodeId(hashed);
 * // => "550e8400-e29b-41d4-a716-446655440000"
 */

import Sqids from 'sqids';

// ─────────────────────────────────────────────────────────────────────────────
// Configuration - DOIT correspondre au backend
// ─────────────────────────────────────────────────────────────────────────────

const HASHID_SALT = import.meta.env?.VITE_HASHID_SALT || "preditwins_default_salt_change_me_in_prod";
const HASHID_MIN_LENGTH = parseInt(import.meta.env?.VITE_HASHID_MIN_LENGTH || "10", 10);
const HASHID_ALPHABET = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789";

/**
 * Mélange l'alphabet de manière déterministe basée sur le salt.
 * DOIT correspondre exactement à la logique Python.
 */
function generateShuffledAlphabet(salt) {
  const alphabet = HASHID_ALPHABET.split('');
  const saltBytes = new TextEncoder().encode(salt);
  
  for (let i = alphabet.length - 1; i > 0; i--) {
    const j = saltBytes[i % saltBytes.length] % (i + 1);
    [alphabet[i], alphabet[j]] = [alphabet[j], alphabet[i]];
  }
  
  return alphabet.join('');
}

// Instance Sqids
const sqids = new Sqids({
  alphabet: generateShuffledAlphabet(HASHID_SALT),
  minLength: HASHID_MIN_LENGTH
});

// ─────────────────────────────────────────────────────────────────────────────
// Helpers UUID
// ─────────────────────────────────────────────────────────────────────────────

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Vérifie si une string est un UUID valide.
 */
export function isUuid(value) {
  return typeof value === 'string' && UUID_REGEX.test(value);
}

/**
 * Convertit un UUID en 4 entiers 32-bit.
 */
function uuidToInts(uuid) {
  // Retire les tirets
  const hex = uuid.replace(/-/g, '');
  
  // Divise en 4 parties de 8 hex chars (32 bits chacune)
  const parts = [];
  for (let i = 0; i < 4; i++) {
    const chunk = hex.slice(i * 8, (i + 1) * 8);
    parts.push(parseInt(chunk, 16));
  }
  
  return parts;
}

/**
 * Convertit 4 entiers 32-bit en UUID.
 */
function intsToUuid(ints) {
  if (ints.length !== 4) {
    throw new Error('Expected 4 integers for UUID reconstruction');
  }
  
  const hex = ints.map(n => n.toString(16).padStart(8, '0')).join('');
  
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20, 32)
  ].join('-');
}

// ─────────────────────────────────────────────────────────────────────────────
// Fonctions principales
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Encode un UUID en identifiant hashé.
 * 
 * @param {string} uuid - UUID à encoder
 * @returns {string} Identifiant hashé
 * 
 * @example
 * encodeId("550e8400-e29b-41d4-a716-446655440000")
 * // => "3QDxWZb4hFdpAyzTCzAvwFx4PDH8"
 */
export function encodeId(uuid) {
  if (!isUuid(uuid)) {
    //console.warn(`encodeId: Invalid UUID "${uuid}", returning as-is`);
    return uuid;
  }
  
  try {
    return sqids.encode(uuidToInts(uuid));
  } catch (e) {
    //console.error('encodeId error:', e);
    return uuid;
  }
}

/**
 * Décode un identifiant hashé en UUID.
 * 
 * @param {string} hashed - Identifiant hashé
 * @returns {string|null} UUID ou null si invalide
 * 
 * @example
 * decodeId("3QDxWZb4hFdpAyzTCzAvwFx4PDH8")
 * // => "550e8400-e29b-41d4-a716-446655440000"
 */
export function decodeId(hashed) {
  if (!hashed || typeof hashed !== 'string') {
    return null;
  }
  
  try {
    const ints = sqids.decode(hashed);
    if (!ints || ints.length !== 4) {
      return null;
    }
    return intsToUuid(ints);
  } catch (e) {
   // console.error('decodeId error:', e);
    return null;
  }
}

/**
 * Décode intelligemment: si c'est déjà un UUID, le retourne tel quel.
 * Utile pendant la période de transition.
 * 
 * @param {string} value - UUID ou identifiant hashé
 * @returns {string|null} UUID ou null si invalide
 */
export function smartDecode(value) {
  if (!value) return null;
  
  // Si c'est déjà un UUID, le retourne normalisé
  if (isUuid(value)) {
    return value.toLowerCase();
  }
  
  // Sinon, tente de décoder
  return decodeId(value);
}

/**
 * Vérifie si une string est un ID hashé valide.
 */
export function isValidHashedId(hashed) {
  return decodeId(hashed) !== null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers pour les objets
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_ID_FIELDS = ['id', 'player_id', 'playerId', 'club_id', 'clubId', 
                          'user_id', 'userId', 'event_id', 'eventId',
                          'survey_id', 'surveyId', 'simulation_id', 'simulationId'];

/**
 * Encode les champs ID d'un objet.
 * 
 * @param {Object} data - Objet avec des UUIDs
 * @param {string[]} idFields - Champs à encoder (optionnel)
 * @returns {Object} Objet avec IDs hashés
 */
export function hashObjectIds(data, idFields = DEFAULT_ID_FIELDS) {
  if (!data || typeof data !== 'object') return data;
  
  const result = { ...data };
  
  for (const field of idFields) {
    if (result[field] && isUuid(result[field])) {
      result[field] = encodeId(result[field]);
    }
  }
  
  return result;
}

/**
 * Encode les IDs dans une liste d'objets.
 * 
 * @param {Object[]} items - Liste d'objets
 * @param {string[]} idFields - Champs à encoder (optionnel)
 * @returns {Object[]} Liste avec IDs hashés
 */
export function hashListIds(items, idFields = DEFAULT_ID_FIELDS) {
  if (!Array.isArray(items)) return items;
  return items.map(item => hashObjectIds(item, idFields));
}

/**
 * Décode les champs ID d'un objet.
 * 
 * @param {Object} data - Objet avec des IDs hashés
 * @param {string[]} idFields - Champs à décoder (optionnel)
 * @returns {Object} Objet avec UUIDs
 */
export function unhashObjectIds(data, idFields = DEFAULT_ID_FIELDS) {
  if (!data || typeof data !== 'object') return data;
  
  const result = { ...data };
  
  for (const field of idFields) {
    if (result[field] && !isUuid(result[field])) {
      const decoded = decodeId(result[field]);
      if (decoded) {
        result[field] = decoded;
      }
    }
  }
  
  return result;
}

export default {
  encodeId,
  decodeId,
  smartDecode,
  isUuid,
  isValidHashedId,
  hashObjectIds,
  hashListIds,
  unhashObjectIds
};
